import { useState, useEffect, useRef } from 'react';
import { Input, Button, Collapse } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { postLoadBalancesReport } from '@src/store/slice/balanceUploadDetailSlice';
import { useDispatch, useSelector } from 'react-redux';
import CustomDatePicker from '@src/components/CustomDatePicker';
import CheckboxComponent from '@src/components/CheckboxComponent';
import { VouIdCode } from '@src/store/enums';
import { useText, Text } from '@src/components';

const { Panel } = Collapse;

const FilterMenu = ({
  setFilteredText,
  filteredText,
  panels,
  listenClickCheckbox,
  handleFilterMenuClick,
  addressList,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  refNo,
  setRefNo,
  cardNumber,
  setCardNumber,
  nameSurname,
  setNameSurname,
  deliveryId,
  setDeliveryId,
  orderNumber,
  setOrderNumber,
  invoiceNumber,
  setInvoiceNumber,
  checkedList,
  setCheckedList,
  pagination,
  segmentId,
  vouId,
  setFilteredObject = null,
}) => {
  const dispatch = useDispatch();
  const { selectedCustomer } = useSelector((state) => state?.customer);
  const [activeKey, setActiveKey] = useState(['0']);
  const isFirstRender = useRef(true);
  const enterText = useText('enter')
  const filterMenuList = [
    {
      valueStart: startDate,
      valueEnd: endDate,
      setValueStart: setStartDate,
      setValueEnd: setEndDate,
      header: useText('balanceActiveDateRange'),
      headerStart: useText('startDate'),
      headerEnd: useText('endDate'),
      type: 'date',
      key: 'startDate',
    },
    {
      value: refNo,
      setValue: setRefNo,
      header: useText('referenceNo'),
      type: 'text',
      key: 'refNo',
    },
    {
      value: cardNumber,
      setValue: setCardNumber,
      header: useText('cardNumber5'),
      type: 'number',
      key: 'cardNumber',
    },
    {
      value: nameSurname,
      setValue: setNameSurname,
      header: useText('nameSurname'),
      type: 'text',
      key: 'nameSurname',
    },
    {
      value: deliveryId,
      setValue: setDeliveryId,
      header: useText('deliveryAddress2'),
      type: 'checkbox',
      key: 'deliveryId',
    },
    {
      value: orderNumber,
      setValue: setOrderNumber,
      header: useText('orderNo'),
      type: 'number',
      key: 'orderNumber',
    },
    {
      value: invoiceNumber,
      setValue: setInvoiceNumber,
      header: useText('invoiceNumber'),
      type: 'number',
      key: 'invoiceNumber',
    },
  ];

  const handleCollapseChange = (key) => {
    setActiveKey(key);
  };

  const clickFilter = async () => {
    const checkeds = checkedList.map((checked) => ({ addressId: checked }));
    const filteredObject = {
      refNo,
      cardNumber,
      nameSurname,
      addressIdList: checkeds.length < 1 ? null : checkeds,
      orderNumber,
      invoiceNumber,
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
    };
    setFilteredObject(filteredObject);
    await dispatch(
      postLoadBalancesReport({
        customerId: selectedCustomer?.customerId,
        segmentId: segmentId,
        refNo,
        cardNumber,
        nameSurname,
        addressIdList: checkeds.length < 1 ? null : checkeds,
        orderNumber,
        invoiceNumber,
        startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
        endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
        page: pagination?.current ?? 1,
        pageSize: pagination?.pageSize ?? 10,
        vouId: vouId,
      }),
    );
    setActiveKey([]);
    handleFilterMenuClick();
  };

  const clearFilter = () => {
    setRefNo(null);
    setCardNumber(null);
    setNameSurname(null);
    setDeliveryId([]);
    setOrderNumber(null);
    setInvoiceNumber(null);
    setStartDate(null);
    setEndDate(null);
    setActiveKey([]);
    setCheckedList([]);
    setFilteredObject(null);
    handleFilterMenuClick();
  };

  const checkboxClick = (checked, addressId) => {
    if (checked) {
      setDeliveryId([...deliveryId, { addressId: addressId }]);
      setCheckedList([...checkedList, addressId]);
    } else {
      const updatedAddresses = deliveryId.filter((address) => address.addressId !== addressId);
      setDeliveryId(updatedAddresses);
      const addressIds = updatedAddresses.map((address) => address.addressId);
      setCheckedList(addressIds);
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (
      refNo === null &&
      cardNumber === null &&
      nameSurname === null &&
      deliveryId.length < 1 &&
      orderNumber === null &&
      invoiceNumber === null &&
      startDate === null &&
      endDate === null
    ) {
      clickFilter();
    }
  }, [refNo, cardNumber, nameSurname, deliveryId, orderNumber, invoiceNumber, startDate, endDate]);

  return (
    <div className="custom-filter-menu">
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span className="custom-filter-menu-filtrele"><Text t="filter2" /></span>
        <button onClick={clearFilter} className="custom-filter-menu-clear">
          <Text t="clean" /></button>
      </div>
      <Collapse onChange={handleCollapseChange} activeKey={activeKey} expandIconPosition="right">
        {filterMenuList.map(
          (filter) =>
            (filter.type !== 'checkbox' ||
              (filter.type === 'checkbox' && addressList?.length > 0)) && (
              <Panel
                key={filter.key}
                header={
                  !(filter.type === 'checkbox' ? checkedList.length >= 1 : filter.value) ? (
                    filter.header
                  ) : (
                    <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      {filter.header}
                      <div
                        style={{
                          height: '20px',
                          width: '20px',
                          marginLeft: '4px',
                          borderRadius: '50%',
                          backgroundColor: '#f5222d',
                          textAlign: 'center',
                          color: 'white',
                        }}
                      >
                        {filter.type === 'checkbox' ? checkedList.length : '1'}
                      </div>
                    </span>
                  )
                }
                className={
                  activeKey?.filter((item) => item === filter.key).length > 0
                    ? 'custom-demands-dropdown-item-active'
                    : 'custom-demands-dropdown-item'
                }
              >
                {filter.type === 'date' ? (
                  <>
                    <CustomDatePicker
                      height={36}
                      style={{ maringBottom: '12px' }}
                      placeholder={filter.headerStart}
                      value={startDate}
                      onChange={(value) => filter.setValueStart(value)}
                      disabledDate={(current) => current && endDate && current?.isAfter(endDate)}
                    />
                    <CustomDatePicker
                      height={36}
                      placeholder={filter.headerEnd}
                      value={endDate}
                      onChange={(value) => filter.setValueEnd(value)}
                      disabledDate={(current) =>
                        current && startDate && current.isBefore(startDate)
                      }
                    />
                  </>
                ) : filter.type === 'checkbox' ? (
                  addressList.map((adress, index) => (
                    <CheckboxComponent
                      item={adress}
                      key={`${adress?.key}-${index}`}
                      list={checkedList}
                      clickCheckbox={(e) => checkboxClick(e.target.checked, adress.key)}
                    />
                  ))
                ) : (
                  <Input
                    id="demandsFilterSearch"
                    name="demandsFilterSearch"
                    type={filter.type}
                    placeholder={filter.header + enterText}
                    prefix={<SearchOutlined />}
                    className="custom-filter-menu-search no-margin"
                    value={filter.value}
                    onChange={(e) => filter.setValue(e.target.value)}
                  />
                )}
              </Panel>
            ),
        )}
      </Collapse>
      <Button
        type="primary"
        onClick={clickFilter}
        block
        className="custom-filter-menu-filter-button"
      >
        <Text t="filter2" />
      </Button>
    </div>
  );
};

export default FilterMenu;

FilterMenu.propTypes = {
  setFilteredText: PropTypes.any,
  filteredText: PropTypes.string,
  panels: PropTypes.array,
  listenClickCheckbox: PropTypes.any,
  handleFilterMenuClick: PropTypes.any,
  addressList: PropTypes.any,
  startDate: PropTypes.any,
  setStartDate: PropTypes.any,
  endDate: PropTypes.any,
  setEndDate: PropTypes.any,
  refNo: PropTypes.any,
  setRefNo: PropTypes.any,
  cardNumber: PropTypes.any,
  setCardNumber: PropTypes.any,
  nameSurname: PropTypes.any,
  setNameSurname: PropTypes.any,
  deliveryId: PropTypes.any,
  setDeliveryId: PropTypes.any,
  orderNumber: PropTypes.any,
  setOrderNumber: PropTypes.any,
  invoiceNumber: PropTypes.any,
  setInvoiceNumber: PropTypes.any,
  checkedList: PropTypes.any,
  setCheckedList: PropTypes.any,
  pagination: PropTypes.any,
  segmentId: PropTypes.any,
  vouId: PropTypes.any,
  setFilteredObject: PropTypes.func,
};
